import { useState, type FormEvent, type JSX } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Badge, Button, toast } from '@caratuva/ui';
import type { PayoutBankAccountInput } from '@caratuva/shared-types';
import {
  getKybReceiverDetail,
  getKybSubmission,
  getPayoutBankAccountDetails,
  getPayoutConfig,
  savePayoutConfig,
  type KybReceiverDetailView,
} from '../lib/api';
import { BankAccountFields, FIELD_LABELS, railLabel } from '../components/bank-account-fields';

/*
 * Bank account — where the org's own settlements land (any BlindPay rail). Split
 * out of the former Payout page; business verification now lives on /onboarding.
 * This page is gated on an approved KYB: the settlement partner will only
 * register a bank account against a verified receiver.
 */

export const Route = createFileRoute('/_app/bank-account')({
  component: BankAccountPage,
});

function BankAccountPage(): JSX.Element {
  const intl = useIntl();

  const kybQuery = useQuery({
    queryKey: ['kyb-submission'],
    queryFn: ({ signal }) => getKybSubmission(signal),
  });

  const detailQuery = useQuery({
    queryKey: ['kyb-receiver-detail'],
    queryFn: ({ signal }) => getKybReceiverDetail(signal),
    enabled: kybQuery.data != null,
  });

  const approved = kybQuery.data?.status === 'approved';

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({ id: 'bankAccount.title', defaultMessage: 'Bank account' })}
        </h1>
        <p className="mt-1 text-sm text-navy-600">
          {intl.formatMessage({
            id: 'bankAccount.subtitle',
            defaultMessage:
              'Register the bank account where your settlements land. The details are held by our settlement partner — we never store them ourselves.',
          })}
        </p>
      </header>

      {kybQuery.isLoading ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({ id: 'payout.loading', defaultMessage: 'Loading...' })}
        </p>
      ) : kybQuery.isError ? (
        <p className="text-sm text-red-700">
          {intl.formatMessage({
            id: 'bankAccount.error.load',
            defaultMessage: 'Could not load bank account status. Refresh to try again.',
          })}
        </p>
      ) : approved ? (
        <BankAccountCard detail={detailQuery.data ?? null} />
      ) : (
        <OnboardingRequiredCard />
      )}
    </section>
  );
}

// Gate shown until the org's KYB is approved — a bank account can only be
// registered against a verified settlement-partner receiver.
function OnboardingRequiredCard(): JSX.Element {
  const intl = useIntl();
  return (
    <div className="max-w-xl space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4">
      <h2 className="text-base font-semibold text-amber-900">
        {intl.formatMessage({
          id: 'bankAccount.gate.title',
          defaultMessage: 'Finish onboarding first',
        })}
      </h2>
      <p className="text-sm text-amber-800">
        {intl.formatMessage({
          id: 'bankAccount.gate.body',
          defaultMessage:
            'Complete business verification (KYB) before registering a bank account. Once your business is verified, come back here to register where settlements land.',
        })}
      </p>
      <div className="flex justify-end">
        <Button asChild>
          <Link to="/onboarding">
            {intl.formatMessage({
              id: 'bankAccount.gate.cta',
              defaultMessage: 'Go to Onboarding',
            })}
          </Link>
        </Button>
      </div>
    </div>
  );
}

function BankAccountCard({ detail }: { detail: KybReceiverDetailView | null }): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['payout-config'],
    queryFn: ({ signal }) => getPayoutConfig(signal),
  });

  const [payout, setPayout] = useState<PayoutBankAccountInput | null>(null);
  const [editing, setEditing] = useState(false);

  const save = useMutation({
    mutationFn: () => savePayoutConfig(payout!),
    onSuccess: () => {
      setPayout(null);
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['payout-config'] });
      toast.success(
        intl.formatMessage({
          id: 'payout.saved',
          defaultMessage: 'Payout settings saved.',
        }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    if (payout == null || save.isPending) return;
    save.mutate();
  }

  const hasBankAccount = Boolean(query.data?.hasBankAccount);
  const showForm = !hasBankAccount || editing;
  const defaultName = detail?.legalName ?? undefined;

  const detailsQuery = useQuery({
    queryKey: ['payout-config-details'],
    queryFn: ({ signal }) => getPayoutBankAccountDetails(signal),
    enabled: hasBankAccount && !editing,
  });

  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-md border border-navy-200 bg-white p-4">
        <div className="flex items-center gap-3 text-sm">
          <span className="font-medium text-navy-900">
            {intl.formatMessage({
              id: 'payout.current',
              defaultMessage: 'Current configuration',
            })}
          </span>
          <Badge variant="default">
            {intl.formatMessage({ id: 'payout.kyb.approved', defaultMessage: 'KYB: approved' })}
          </Badge>
          <Badge variant={hasBankAccount ? 'default' : 'outline'}>
            {hasBankAccount
              ? intl.formatMessage({
                  id: 'bankAccount.summary.set',
                  defaultMessage: 'Bank account: set',
                })
              : intl.formatMessage({
                  id: 'bankAccount.summary.notSet',
                  defaultMessage: 'Bank account: not set',
                })}
          </Badge>
        </div>
      </div>

      {showForm ? (
        <form
          onSubmit={onSubmit}
          className="max-w-xl space-y-4 rounded-md border border-navy-200 bg-white p-4"
        >
          <div>
            <h2 className="text-base font-semibold text-navy-900">
              {hasBankAccount
                ? intl.formatMessage({
                    id: 'bankAccount.updateTitle',
                    defaultMessage: 'Update bank account',
                  })
                : intl.formatMessage({
                    id: 'bankAccount.registerTitle',
                    defaultMessage: 'Register bank account',
                  })}
            </h2>
            <p className="mt-1 text-sm text-navy-600">
              {intl.formatMessage({
                id: 'bankAccount.formSubtitle',
                defaultMessage:
                  'Our settlement partner registers this account and settles to it on every invoice. Caratuva never stores the account details.',
              })}
            </p>
          </div>

          <BankAccountFields onChange={setPayout} {...(defaultName ? { defaultName } : {})} />

          <div className="flex justify-end gap-2">
            {hasBankAccount ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setPayout(null);
                }}
                disabled={save.isPending}
              >
                {intl.formatMessage({ id: 'payout.cancel', defaultMessage: 'Cancel' })}
              </Button>
            ) : null}
            <Button type="submit" disabled={payout == null || save.isPending}>
              {save.isPending
                ? intl.formatMessage({ id: 'payout.saving', defaultMessage: 'Saving...' })
                : intl.formatMessage({ id: 'payout.save', defaultMessage: 'Save' })}
            </Button>
          </div>
        </form>
      ) : (
        <div className="max-w-xl space-y-3 rounded-md border border-navy-200 bg-white p-4">
          <div>
            <h2 className="text-base font-semibold text-navy-900">
              {intl.formatMessage({
                id: 'bankAccount.setTitle',
                defaultMessage: 'Bank account configured',
              })}
            </h2>
            <p className="mt-1 text-sm text-navy-600">
              {intl.formatMessage({
                id: 'bankAccount.setBody',
                defaultMessage:
                  'Our settlement partner holds these settlement details on file and pays out to them on every invoice. Update them if your settlement details change.',
              })}
            </p>
          </div>

          {detailsQuery.isLoading ? (
            <p className="text-sm text-navy-600">
              {intl.formatMessage({
                id: 'bankAccount.details.loading',
                defaultMessage: 'Loading bank details...',
              })}
            </p>
          ) : detailsQuery.isError || detailsQuery.data?.available === false ? (
            <p className="text-sm text-amber-700">
              {intl.formatMessage({
                id: 'bankAccount.details.unavailable',
                defaultMessage: 'Bank details are temporarily unavailable. Try again shortly.',
              })}
            </p>
          ) : detailsQuery.data?.bankAccount ? (
            <dl className="grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-navy-600">
                  {intl.formatMessage({
                    id: 'bankAccount.details.rail',
                    defaultMessage: 'Payment rail',
                  })}
                </dt>
                <dd className="mt-1 text-sm text-navy-900">
                  {(() => {
                    const rail = railLabel(detailsQuery.data.bankAccount.type);
                    return rail
                      ? intl.formatMessage({ id: rail.id, defaultMessage: rail.defaultMessage })
                      : detailsQuery.data.bankAccount.type;
                  })()}
                </dd>
              </div>
              {detailsQuery.data.bankAccount.name != null ? (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-navy-600">
                    {intl.formatMessage({
                      id: 'bankAccountFields.name',
                      defaultMessage: FIELD_LABELS.name ?? 'Account name',
                    })}
                  </dt>
                  <dd className="mt-1 text-sm text-navy-900">
                    {detailsQuery.data.bankAccount.name}
                  </dd>
                </div>
              ) : null}
              {detailsQuery.data.bankAccount.details.map((field) => (
                <div key={field.key}>
                  <dt className="text-xs font-medium uppercase tracking-wide text-navy-600">
                    {intl.formatMessage({
                      id: `bankAccountFields.${field.key}`,
                      defaultMessage: FIELD_LABELS[field.key] ?? field.key,
                    })}
                  </dt>
                  <dd className="mt-1 break-all text-sm text-navy-900">{field.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}

          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={() => setEditing(true)}>
              {intl.formatMessage({
                id: 'bankAccount.update',
                defaultMessage: 'Update bank account',
              })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
